import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { Task, TaskStatus } from "@/graphql/__generated__/graphql";
import { useState } from "react";
import { TaskDialogContent } from "./Dialogs";
import { Separator } from "@/components/ui/separator";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Skeleton } from "@/components/ui/skeleton";
import TaskComponent from "./TaskComponent";
import { useTranslation } from "react-i18next";
import { useDroppable } from "@dnd-kit/core";

interface ColumnComponentProps {
  title: string;
  taskStatus: TaskStatus;
  tasks: Task[];
  type: "user" | "tender";
  loading?: boolean;
  canEdit?: boolean;
}

/**
 * Component to render a column of tasks
 * @param title Title of the column
 * @param taskStatus Task status of the column
 * @param tasks List of tasks
 * @param type "user" or "tender" - used to determine the rendering of the component
 * @param loading Loading state of the component
 * @param canEdit Boolean to determine if the user can edit the tasks
 * @returns JSX.Element
 */
function ColumnComponent({ title, taskStatus, tasks, type, loading, canEdit }: ColumnComponentProps) {

  const { t } = useTranslation(["components"]);
  const [isOpen, setIsOpen] = useState(false);

  // Droppable hook to make the column droppable
  const { setNodeRef } = useDroppable({ id: taskStatus });

  return (
    <Card ref={setNodeRef} className="flex flex-col w-1/3 h-full">
      <div>
        <div className="flex flex-wrap gap-2 items-center justify-between py-2 px-4">
          <h2 className="font-semibold">
            {title}
          </h2>
          {
            (type === "tender" && canEdit) && (
              <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline">{t("tasksboard.addtaskbtn")}</Button>
                </DialogTrigger>
                <TaskDialogContent taskStatus={taskStatus} type={type} action="create" canEdit={canEdit} setIsOpen={setIsOpen} />
              </Dialog>
            )
          }
        </div>
        <Separator />
      </div>
      <SortableContext
        items={tasks.sort((a, b) => b.order - a.order).map((t) => t.id)}
        strategy={verticalListSortingStrategy}
      >
        <CardContent className="flex flex-col grow gap-4 p-4 overflow-y-auto max-h-[75vh]">
          {
            loading ? (
              <>
                {
                  Array.from({ length: 3 }).map((_, index) => (
                    <Skeleton key={index} className="w-full h-[120px] rounded-md" />
                  ))
                }
              </>
            ) : tasks.length === 0 ? (
              <div className="text-center text-sm text-slate-400">
                {t("tasksboard.notasks")}
              </div>
            ) : (
              tasks.map((task) => (
                <TaskComponent key={task.id} task={task} type={type} canEdit={canEdit} />
              ))
            )
          }
        </CardContent>
      </SortableContext>
    </Card>
  );
}
export default ColumnComponent;
